/**
 * N뷰 비주얼 헐 — 방위가 다른 실루엣 N장으로 깎는 입체.
 *
 * 세 뷰(0·90·180°)는 상자 모양으로만 깎인다: 대각선 방향의 살이 남아 몸통 단면이
 * 둥근 캐릭터도 네모나게 나온다. 45° 뷰 넷을 더하면 단면이 팔각형이 되고, 열여섯이면
 * 거의 원이다. 이게 Meshy 가 여덟 장을 요구하는 이유다.
 *
 * 좌표: 높이 1(바닥 0), 뷰 i 의 화면 오른쪽 r_i = (cosθ, 0, −sinθ). 복셀 p 가 뷰 i 의
 * 실루엣 안이려면 s_i = (p·r_i)/w_i + 0.5 가 실루엣 안이어야 한다. w_i 는 그 뷰의
 * 실루엣 폭(높이 1 기준) — 실루엣 극점이 곧 투영 폭이라 정규화가 저절로 맞는다.
 *
 * 텍스처는 여기서 UV 를 굽지 않는다. 셰이더가 정점의 바인드 포즈 위치를 각 뷰에
 * 투영해 법선이 향하는 쪽 뷰들을 섞는다(stylize.ts `MAP_FRAGMENT`). 여기서는
 * 노멀·ORM 맵용 대표 UV(법선이 가장 잘 받는 뷰)만 만든다.
 */

import * as THREE from 'three';
import { blur3, dropIslands, quadsToTriangles, relaxQuadMesh, surfaceNets, type Grid3 } from '../mesh/surfaceNets.ts';
import { gaussianBlur, luminanceField, mapField, normalizeField } from '../imaging/raster.ts';
import { distanceTransform } from '../imaging/silhouette.ts';
import type { Field } from '../types.ts';
import type { ViewAtlas } from './atlas.ts';
import { viewBasis, viewSampler, type TurnaroundSet, type ViewImage } from './views.ts';
import { buildCarveMap, sampleCarve, type CarveMap } from './depthCarve.ts';

export interface HullParams {
  /** 세로 셀 개수 */
  resolution: number;
  /** 라플라시안 스무딩 횟수 */
  smooth: number;
  /** 필드 블러 반경(셀) */
  blur: number;
  /** 요철 변위 진폭(높이 1 기준) */
  relief: number;
  /** 측면 뷰의 캐릭터가 이미지 왼쪽을 보는가. false 면 측면 계열 뷰의 s 를 거울로. */
  sideFacesLeft: boolean;
  /** 깊이 깎기 — 두께 대비 최대 비율 0..1. 0 이면 실루엣 헐만. */
  carve: number;
  /**
   * 실루엣 관용 — 몇 개 뷰까지 "밖"이라 해도 살려 둘지. 0 = 엄격한 교집합.
   * 생성 이미지는 장마다 팔 위치가 조금씩 다르다. 엄격한 교집합은 그 불일치가 곧 삭제라
   * 가는 팔이 통째로 사라진다(실측: 고블린 8뷰 — 팔이 조각만 남음). 1이면 8뷰 중 하나가
   * 틀려도 남는다. 값은 "k+1 번째로 작은 점유".
   */
  tolerance: number;
  /**
   * 실루엣 팽창(높이 1 기준). 엄격한 교집합(tolerance 0)은 뷰마다 몇 px 어긋난 발목을 끊고(실측:
   * 엠버 뱅가드 — 발이 떨어짐), 관용 1 은 한 뷰가 틀린 곳을 다른 뷰 두께만큼 통째로 살려 손이
   * 벙어리장갑이 된다. 팽창은 그 사이다: 실루엣을 ε 만큼만 넓혀 교집합한다 — 작은 불일치는
   * 살고 큰 불일치는 ε 이상 못 자란다.
   */
  dilate?: number;
  /**
   * 깊이 요철(높이 1 기준 진폭). 실루엣 헐은 얼굴이 달걀이다 — 코·눈두덩·턱이 없다. 정면 텍스처를
   * 달걀에 입히면 옆에서 볼 때 일그러진다(실측: 엠버 뱅가드). 뷰별 신경망 깊이의 **고주파**(저주파는
   * 헐이 이미 가진 부피)를 법선 방향 변위로 얹으면 코가 나오고 눈두덩이 들어간다. 깎기가 "안쪽으로만"
   * 이라면 이건 양방향 잔 굴곡이다.
   */
  depthRelief?: number;
  /** 뷰별 깊이 필드(뷰 이미지 해상도, 1 = 가까움). 없는 뷰는 null. */
  depths?: (Field | null)[];
}

export const DEFAULT_HULL_PARAMS: HullParams = {
  resolution: 144,
  smooth: 3,
  blur: 1,
  relief: 0.02,
  sideFacesLeft: true,
  carve: 0,
  tolerance: 0,
};

/** 셰이더 투영에 필요한 뷰 파라미터. 최대 8뷰. */
export interface ProjectionUniforms {
  count: number;
  /** [rx, rz, 1/w, 0] × 8 */
  r: Float32Array;
  /** [dx, dz] × 8 */
  d: Float32Array;
  /** [u0, v0, du, dv] × 8 */
  rect: Float32Array;
  /** 최종 오브젝트 좌표 → 헐 좌표: p = (P - offset) * scale */
  offset: THREE.Vector3;
  scale: number;
}

export interface HullMesh {
  geometry: THREE.BufferGeometry;
  vertexCount: number;
  triangleCount: number;
  extent: { x: number; y: number; z: number };
  viewShare: Record<string, number>;
  /** (정점, 뷰) 쌍 중 가림으로 걸러진 비율 0..1. 0 이면 가시성이 아무것도 안 바꿨다는 뜻. */
  occluded: number;
  /** 깊이 깎기에 참여한 뷰 수 */
  carvedViews: number;
  /** 텍스처를 UV 아틀라스로 구웠는가(그러면 셰이더 투영 대신 보통 uv 를 쓴다) */
  baked?: boolean;
  /** 베이크 뒤 차트(뷰)별 삼각형 수 */
  chartShare?: number[];
  projection: ProjectionUniforms;
}

export const MAX_VIEWS = 8;

/** 직전 헐의 인덱스 위상 — 테스트가 다양체를 검사한다. */
export let lastIndexed: { positions: Float32Array; index: Int32Array } | null = null;

/** 뷰의 요철 = 휘도 고주파. 0.5 가 평균. 마스크 밖은 0.5. */
function reliefField(view: ViewImage): Field {
  const luma = luminanceField(view.raster);
  const base = gaussianBlur(luma, Math.max(4, Math.max(view.raster.width, view.raster.height) * 0.04));
  const fine = gaussianBlur(luma, 2.2);
  const hp = normalizeField(mapField(fine, (v, i) => v - base.data[i]!), 0.03, 0.97, view.mask);
  return mapField(hp, (v, i) => (view.mask.data[i]! >= 0.5 ? v : 0.5));
}

/** 뷰 깊이의 고주파 = 국소 돌출. 0.5 가 평균, 마스크 밖 0.5. 깊이 필드 해상도 그대로(뷰 래스터와 다를 수 있다). */
function depthReliefField(view: ViewImage, depth: Field): Field {
  const sx = view.raster.width / depth.width, sy = view.raster.height / depth.height;
  const maskD: Field = { width: depth.width, height: depth.height, data: new Float32Array(depth.width * depth.height) };
  for (let y = 0; y < depth.height; y++) {
    const yy = Math.min(view.mask.height - 1, Math.round(y * sy));
    for (let x = 0; x < depth.width; x++) {
      const xx = Math.min(view.mask.width - 1, Math.round(x * sx));
      maskD.data[y * depth.width + x] = view.mask.data[yy * view.mask.width + xx]!;
    }
  }
  const base = gaussianBlur(depth, Math.max(3, depth.height * 0.06));
  const hp = normalizeField(mapField(depth, (v, i) => v - base.data[i]!), 0.03, 0.97, maskD);
  return mapField(hp, (v, i) => (maskD.data[i]! >= 0.5 ? v : 0.5));
}

/** 뷰 래스터 좌표계의 (s,t) 를 다른 해상도의 필드에서 샘플. */
function sampleScaledAt(field: Field, view: ViewImage, s: number, t: number, outside: number): number {
  if (s < 0 || s > 1 || t < 0 || t > 1) return outside;
  const bw = Math.max(1, view.bbox.x1 - view.bbox.x0);
  const bh = Math.max(1, view.bbox.y1 - view.bbox.y0);
  const xr = view.bbox.x0 + s * bw, yr = view.bbox.y1 - t * bh;
  const xi = Math.round(xr * (field.width / view.raster.width)), yi = Math.round(yr * (field.height / view.raster.height));
  if (xi < 0 || yi < 0 || xi >= field.width || yi >= field.height) return outside;
  return field.data[yi * field.width + xi]!;
}

function sampleFieldAt(field: Field, view: ViewImage, s: number, t: number, outside: number): number {
  if (s < 0 || s > 1 || t < 0 || t > 1) return outside;
  const bw = Math.max(1, view.bbox.x1 - view.bbox.x0);
  const bh = Math.max(1, view.bbox.y1 - view.bbox.y0);
  const xi = Math.round(view.bbox.x0 + s * bw);
  const yi = Math.round(view.bbox.y1 - t * bh);
  if (xi < 0 || yi < 0 || xi >= field.width || yi >= field.height) return outside;
  return field.data[yi * field.width + xi]!;
}

/**
 * 뷰별 가시성 — 정점이 그 뷰 카메라에서 실제로 보이는가.
 *
 * 법선만으로 뷰를 고르면 팔 뒤에 가린 몸통 옆구리에 **팔 그림**이 찍힌다. 각 뷰에서
 * 헐을 소프트웨어로 래스터해 깊이맵(카메라 쪽으로 가장 먼 점)을 만들고, 정점의 깊이가
 * 그 픽셀 주변 3×3 최솟값보다 eps 안에 있으면 보이는 것으로 친다. 결과는 0..1 (부드러운
 * 문턱) 로 정점 속성에 실려 셰이더의 뷰 가중치에 곱한다.
 */
function viewVisibility(
  positions: ArrayLike<number>,
  tri: Int32Array | number[],
  basis: { rx: number; rz: number; dx: number; dz: number; w: number }[],
  N = 256,
  eps = 0.02,
): Float32Array[] {
  const vcount = positions.length / 3;
  const out: Float32Array[] = [];
  const depth = new Float32Array(N * N);
  for (const b of basis) {
    depth.fill(-Infinity);
    const proj = (v: number): [number, number, number] => {
      const x = positions[v * 3]!, y = positions[v * 3 + 1]!, z = positions[v * 3 + 2]!;
      return [((x * b.rx + z * b.rz) / b.w + 0.5) * (N - 1), y * (N - 1), x * b.dx + z * b.dz];
    };
    for (let t = 0; t < tri.length; t += 3) {
      const [ax, ay, ad] = proj(tri[t]!), [bx, by, bd] = proj(tri[t + 1]!), [cx, cy, cd] = proj(tri[t + 2]!);
      const x0 = Math.max(0, Math.floor(Math.min(ax, bx, cx))), x1 = Math.min(N - 1, Math.ceil(Math.max(ax, bx, cx)));
      const y0 = Math.max(0, Math.floor(Math.min(ay, by, cy))), y1 = Math.min(N - 1, Math.ceil(Math.max(ay, by, cy)));
      const area = (bx - ax) * (cy - ay) - (cx - ax) * (by - ay);
      if (Math.abs(area) < 1e-9) continue;
      const inv = 1 / area;
      for (let py = y0; py <= y1; py++) {
        for (let px = x0; px <= x1; px++) {
          const w0 = ((bx - px) * (cy - py) - (cx - px) * (by - py)) * inv;
          const w1 = ((cx - px) * (ay - py) - (ax - px) * (cy - py)) * inv;
          const w2 = 1 - w0 - w1;
          // 픽셀 반 칸의 여유를 둔다 — 얇은 삼각형이 빠지면 가림 판정에 구멍이 난다.
          if (w0 < -0.02 || w1 < -0.02 || w2 < -0.02) continue;
          const d = w0 * ad + w1 * bd + w2 * cd;
          const i = py * N + px;
          if (d > depth[i]!) depth[i] = d;
        }
      }
    }
    const vis = new Float32Array(vcount);
    for (let v = 0; v < vcount; v++) {
      const [sx, sy, d] = proj(v);
      const px = Math.round(sx), py = Math.round(sy);
      let ref = Infinity;
      for (let oy = -1; oy <= 1; oy++) {
        for (let ox = -1; ox <= 1; ox++) {
          const qx = px + ox, qy = py + oy;
          if (qx < 0 || qy < 0 || qx >= N || qy >= N) continue;
          const dd = depth[qy * N + qx]!;
          if (dd !== -Infinity && dd < ref) ref = dd;
        }
      }
      if (ref === Infinity) { vis[v] = 1; continue; }
      // d ≥ ref − eps 면 보임. eps 폭으로 부드럽게.
      const m = (d - (ref - eps)) / eps + 0.5;
      vis[v] = Math.min(1, Math.max(0, m));
    }
    out.push(vis);
  }
  return out;
}

/** 마스크를 ε px 팽창 — 바깥 거리장으로. ε..ε+1 px 에서 1→0 으로 부드럽게. */
function dilateMask(mask: Field, eps: number): Field {
  const inv: Field = { width: mask.width, height: mask.height, data: new Float32Array(mask.data.length) };
  for (let i = 0; i < inv.data.length; i++) inv.data[i] = mask.data[i]! >= 0.5 ? 0 : 1;
  const dist = distanceTransform(inv, 0.5, false);
  const out: Field = { width: mask.width, height: mask.height, data: new Float32Array(mask.data.length) };
  for (let i = 0; i < out.data.length; i++) {
    const d = dist.data[i]!;
    out.data[i] = mask.data[i]! >= 0.5 ? Math.max(mask.data[i]!, 1) : Math.max(0, Math.min(1, eps + 1 - d));
  }
  return out;
}

export function buildTurnaroundHull(set: TurnaroundSet, atlas: ViewAtlas, params: HullParams): HullMesh {
  const views = set.views.slice(0, MAX_VIEWS);
  if (views.length < 2) throw new Error('뷰가 둘 이상 필요합니다.');

  const basis = views.map((v) => {
    const b = viewBasis(v.azimuth);
    // 측면 계열 뷰가 "오른쪽을 보고" 그려졌으면 s 축을 뒤집는다.
    const flip = !params.sideFacesLeft && Math.abs(Math.sin((v.azimuth * Math.PI) / 180)) > 0.5;
    const dil = params.dilate ?? 0;
    const view = dil > 0 ? { ...v, mask: dilateMask(v.mask, dil * Math.max(1, v.bbox.y1 - v.bbox.y0)) } : v;
    return { ...b, rx: flip ? -b.rx : b.rx, rz: flip ? -b.rz : b.rz, w: v.aspect, sampler: viewSampler(view) };
  });

  // 헐 상자: x 폭은 정면/후면 계열 뷰의 폭, z 깊이는 측면 계열 뷰의 폭. 없으면 최댓값.
  const widthAlong = (fx: number, fz: number) => {
    let best = -1, bestDot = 0.5;
    views.forEach((v, i) => {
      const dot = Math.abs(basis[i]!.rx * fx + basis[i]!.rz * fz);
      if (dot > bestDot) { bestDot = dot; best = i; }
    });
    return best >= 0 ? views[best]!.aspect : Math.max(...views.map((v) => v.aspect));
  };
  const extent = { x: widthAlong(1, 0), y: 1, z: widthAlong(0, 1) };

  const R = Math.max(16, Math.round(params.resolution));
  const h = 1 / R;
  const pad = Math.max(2, params.blur + 2);
  const nx = Math.round(extent.x / h) + 1 + 2 * pad;
  const ny = R + 1 + 2 * pad;
  const nz = Math.round(extent.z / h) + 1 + 2 * pad;
  const toWorld = (i: number, j: number, k: number): [number, number, number] => [
    (i - pad) * h - extent.x / 2, (j - pad) * h, (k - pad) * h - extent.z / 2,
  ];

  // 1) 점유 = 모든 뷰 실루엣의 최솟값. 깎기 맵이 있으면 깊이 경계도 곱한다.
  const tol = Math.max(0, Math.min(basis.length - 2, Math.round(params.tolerance)));
  const silBuf = new Float32Array(MAX_VIEWS);
  const fillGrid = (carves: (CarveMap | null)[] | null): Grid3 => {
    const grid: Grid3 = { nx, ny, nz, data: new Float32Array(nx * ny * nz) };
    for (let k = pad; k < nz - pad; k++) {
      for (let j = pad; j < ny - pad; j++) {
        for (let i = pad; i < nx - pad; i++) {
          const [x, y, z] = toWorld(i, j, k);
          // 실루엣 점유: 관용 k 면 k+1 번째로 작은 값. (k=0 이면 최솟값 = 교집합)
          let occ = 1;
          if (tol > 0) {
            let n0 = 0;
            for (let vi = 0; vi < basis.length; vi++) {
              const b = basis[vi]!;
              const v = b.sampler.sample((x * b.rx + z * b.rz) / b.w + 0.5, y);
              silBuf[vi] = v;
              if (v <= 0 && ++n0 > tol) { occ = 0; break; }
            }
            if (occ > 0) {
              // k+1 번째 최솟값 (뷰 8개 이하 — 삽입 정렬이면 충분)
              const arr = silBuf.subarray(0, basis.length);
              const sorted = Array.from(arr).sort((p, q) => p - q);
              occ = sorted[tol]!;
            }
          } else {
            for (let vi = 0; vi < basis.length; vi++) {
              const b = basis[vi]!;
              occ = Math.min(occ, b.sampler.sample((x * b.rx + z * b.rz) / b.w + 0.5, y));
              if (occ <= 0) break;
            }
          }
          for (let vi = 0; vi < basis.length && occ > 0; vi++) {
            const b = basis[vi]!;
            const s = (x * b.rx + z * b.rz) / b.w + 0.5;
            const cm = carves?.[vi];
            if (cm) {
              const limit = sampleCarve(cm, s, y);
              if (limit !== Infinity) {
                // q 가 경계보다 카메라 쪽이면 빈 공간. 셀 하나 폭으로 부드럽게.
                const q = x * b.dx + z * b.dz;
                // 경계를 한 셀 바깥에 둔다 — 헐 메시는 이완으로 복셀 헐보다 살짝 안쪽이라, 그대로 쓰면 전체가 한 겹 벗겨진다(실측 carve=0.001 에 부피 −1.4%).
                occ = Math.min(occ, Math.min(1, Math.max(0, (limit - q) / h + 1.5)));
                if (occ <= 0) break;
              }
            }
          }
          grid.data[(k * ny + j) * nx + i] = occ;
        }
      }
    }
    return grid;
  };

  // 2) 표면 → 사각형 → 스무딩 → 삼각형.
  const extract = (grid: Grid3) => {
    const raw = surfaceNets(blur3(grid, params.blur), 0.5);
    // 공중에 뜬 조각(뷰 불일치가 낸 발목·손목 파편)은 버린다.
    const nets = dropIslands(raw.positions, raw.quads);
    relaxQuadMesh(nets.positions, nets.quads, params.smooth);
    const positions = nets.positions;
    for (let v = 0; v < positions.length; v += 3) {
      const [x, y, z] = toWorld(positions[v]!, positions[v + 1]!, positions[v + 2]!);
      positions[v] = x; positions[v + 1] = y; positions[v + 2] = z;
    }
    return { nets, positions, tri: quadsToTriangles(nets.quads) };
  };

  let { nets, positions, tri } = extract(fillGrid(null));

  // 1b) 깊이 깎기 — 실루엣 헐을 눈금으로 뷰별 깎기 맵을 만들고 한 번 더 깎는다.
  let carved = 0;
  if (params.carve > 0 && params.depths?.some(Boolean)) {
    const carves = basis.map((b, i) => {
      const d = params.depths?.[i];
      return d ? buildCarveMap(positions, tri, views[i]!, b, d, params.carve) : null;
    });
    const before = positions.length;
    ({ nets, positions, tri } = extract(fillGrid(carves)));
    carved = carves.filter(Boolean).length;
    void before;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(tri);
  geometry.computeVertexNormals();
  lastIndexed = { positions: Float32Array.from(positions), index: Int32Array.from(tri) };

  // 3) 요철 변위. 법선이 향하는 뷰들의 휘도 고주파를 가중 평균해 법선 방향으로.
  const vcount = positions.length / 3;
  const nrm = geometry.getAttribute('normal') as THREE.BufferAttribute;
  // 뷰 가중치용 **과하게 매끈한** 법선. 실루엣의 잔 요철(갑옷 판 틈)을 따라 헐 옆면이
  // 계단처럼 서고, 그 법선으로 뷰를 고르면 줄무늬가 된다(실측: 측면 정점 54% 가 nx<0.5).
  // 그래서 위상은 그대로 두고 좌표만 열 번 더 이완한 복사본의 법선을 따로 실어 보낸다.
  const smoothCopy = positions.slice();
  relaxQuadMesh(smoothCopy, nets.quads, 12, 0.2);
  const smoothGeo = new THREE.BufferGeometry();
  smoothGeo.setAttribute('position', new THREE.Float32BufferAttribute(smoothCopy, 3));
  smoothGeo.setIndex(tri);
  smoothGeo.computeVertexNormals();
  const smoothNormal = new Float32Array((smoothGeo.getAttribute('normal') as THREE.BufferAttribute).array as Float32Array);
  smoothGeo.dispose();
  // 가시성은 요철 전 좌표로 잰다(요철은 ±2% 라 판정을 바꾸지 않는다).
  const visibility = viewVisibility(positions, tri, basis);
  const weightsFor = (nx_: number, ny_: number, nz_: number, v = -1) => {
    const horiz = Math.hypot(nx_, nz_) || 1e-6;
    const hx = nx_ / horiz, hz = nz_ / horiz;
    const w = basis.map((b, i) => Math.pow(Math.max(0, hx * b.dx + hz * b.dz), 3) * (v >= 0 ? 0.03 + 0.97 * visibility[i]![v]! : 1));
    const sum = w.reduce((a, c) => a + c, 0) || 1;
    return w.map((x) => x / sum);
  };
  const stFor = (b: (typeof basis)[number], x: number, y: number, z: number): [number, number] => [
    (x * b.rx + z * b.rz) / b.w + 0.5, y,
  ];

  const depthAmp = params.depthRelief ?? 0;
  const depthReliefs = depthAmp > 0 && params.depths ? views.map((v, i) => (params.depths?.[i] ? depthReliefField(v, params.depths[i]!) : null)) : null;
  if (params.relief > 0 || depthReliefs?.some(Boolean)) {
    const reliefs = views.map(reliefField);
    for (let v = 0; v < vcount; v++) {
      const x = positions[v * 3]!, y = positions[v * 3 + 1]!, z = positions[v * 3 + 2]!;
      const nX = nrm.getX(v), nY = nrm.getY(v), nZ = nrm.getZ(v);
      const w = weightsFor(nX, nY, nZ, v);
      let relief = 0, dRelief = 0, dW = 0;
      basis.forEach((b, i) => {
        if (w[i]! < 1e-3) return;
        const [s, t] = stFor(b, x, y, z);
        relief += w[i]! * sampleFieldAt(reliefs[i]!, views[i]!, s, t, 0.5);
        const dr = depthReliefs?.[i];
        if (dr) { dRelief += w[i]! * sampleScaledAt(dr, views[i]!, s, t, 0.5); dW += w[i]!; }
      });
      // 위·아래를 보는 면은 뷰가 없으니 요철을 약하게.
      const horiz = Math.hypot(nX, nZ);
      const d = (relief - 0.5) * 2 * params.relief * horiz + (dW > 0 ? (dRelief / dW - 0.5) * 2 * depthAmp * horiz : 0);
      positions[v * 3] = x + nX * d;
      positions[v * 3 + 1] = y + nY * d;
      positions[v * 3 + 2] = z + nZ * d;
    }
    (geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    geometry.computeVertexNormals();
  }

  // 4) 대표 UV — 법선이 가장 잘 받는 뷰의 아틀라스 좌표. 노멀·ORM 맵이 쓴다.
  const uv: number[] = [];
  const viewShare: Record<string, number> = {};
  for (let v = 0; v < vcount; v++) {
    const x = positions[v * 3]!, y = positions[v * 3 + 1]!, z = positions[v * 3 + 2]!;
    const w = weightsFor(smoothNormal[v * 3]!, smoothNormal[v * 3 + 1]!, smoothNormal[v * 3 + 2]!, v);
    let best = 0;
    for (let i = 1; i < w.length; i++) if (w[i]! > w[best]!) best = i;
    const key = `${views[best]!.azimuth}°`;
    viewShare[key] = (viewShare[key] ?? 0) + 1;
    const [s, t] = stFor(basis[best]!, x, y, z);
    const r = atlas.rects[best]!;
    uv.push(r.u0 + Math.min(1, Math.max(0, s)) * r.du, r.v0 + Math.min(1, Math.max(0, t)) * r.dv);
  }
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geometry.setAttribute('aShell', new THREE.Float32BufferAttribute(new Float32Array(vcount), 1));
  geometry.setAttribute('aSmoothN', new THREE.Float32BufferAttribute(smoothNormal, 3));
  // 뷰 가시성 0..1 을 vec4 둘에 나눠 싣는다(뷰 0~3, 4~7). 셰이더가 가중치에 곱한다.
  const visA = new Float32Array(vcount * 4), visB = new Float32Array(vcount * 4);
  let hiddenShare = 0;
  for (let v = 0; v < vcount; v++) {
    for (let i = 0; i < 8; i++) {
      const val = i < visibility.length ? visibility[i]![v]! : 0;
      if (i < 4) visA[v * 4 + i] = val; else visB[v * 4 + (i - 4)] = val;
      if (i < visibility.length && val < 0.5) hiddenShare++;
    }
  }
  geometry.setAttribute('aVisA', new THREE.Float32BufferAttribute(visA, 4));
  geometry.setAttribute('aVisB', new THREE.Float32BufferAttribute(visB, 4));
  const occluded = hiddenShare / (vcount * views.length);

  // 5) 높이 2, 중심 원점. 투영 유니폼은 이 변환을 되감는다.
  geometry.scale(2, 2, 2);
  geometry.computeBoundingBox();
  const center = new THREE.Vector3();
  geometry.boundingBox!.getCenter(center);
  geometry.translate(-center.x, -center.y, -center.z);
  geometry.computeBoundingSphere();

  // 헐 좌표 p = (P + center) / 2, 단 헐의 y 는 0..1 이고 x,z 는 중심이 0 이므로
  // 스케일 전 중심 = center/2 → p = P*0.5 + center*0.5.
  const projection: ProjectionUniforms = {
    count: views.length,
    r: new Float32Array(MAX_VIEWS * 4),
    d: new Float32Array(MAX_VIEWS * 2),
    rect: new Float32Array(MAX_VIEWS * 4),
    offset: center.clone().multiplyScalar(-1), // P - offset = P + center
    scale: 0.5,
  };
  basis.forEach((b, i) => {
    projection.r.set([b.rx, b.rz, 1 / b.w, 0], i * 4);
    projection.d.set([b.dx, b.dz], i * 2);
    const rc = atlas.rects[i]!;
    projection.rect.set([rc.u0, rc.v0, rc.du, rc.dv], i * 4);
  });

  return { geometry, vertexCount: vcount, triangleCount: tri.length / 3, extent, viewShare, occluded, carvedViews: carved, projection };
}
